import React, { useEffect, useRef, useState } from "react";
import { JOURNEY_THEMES } from "../data/content";
import { adminListJourneys, adminUpdateJourney, adminUploadImage } from "../lib/api";
import { Check, UploadCloud } from "lucide-react";

const UploadZone = ({ label, onUploaded, testid }) => {
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const inputRef = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const url = await adminUploadImage(file);
      onUploaded(url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid={testid}
      onDragOver={(e) => {
        e.preventDefault();
        setDrag(true);
      }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        handle(e.dataTransfer.files[0]);
      }}
      onClick={() => inputRef.current && inputRef.current.click()}
      className={
        "cursor-pointer border border-dashed px-4 py-3 text-sm flex items-center gap-3 transition-colors duration-300 " +
        (drag ? "border-ink bg-stone-soft/70" : "border-stone hover:border-ink/50")
      }
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        className="hidden"
        onChange={(e) => handle(e.target.files[0])}
      />
      <UploadCloud size={18} className="text-brass shrink-0" />
      <span className="text-ink/60">{busy ? "Uploading…" : label}</span>
      {err && <span className="text-red-700">{err}</span>}
    </div>
  );
};

export default function JourneysAdmin() {
  const [state, setState] = useState({});
  const [savedSlug, setSavedSlug] = useState("");
  const [savingSlug, setSavingSlug] = useState("");

  useEffect(() => {
    adminListJourneys()
      .then((overrides) => {
        const bySlug = {};
        (overrides || []).forEach((o) => (bySlug[o.slug] = o));
        const init = {};
        JOURNEY_THEMES.forEach((t) => {
          const o = bySlug[t.slug] || {};
          init[t.slug] = { hero: o.hero || "", galleryText: (o.gallery || []).join("\n") };
        });
        setState(init);
      })
      .catch(() => {});
  }, []);

  const update = (slug, field, value) =>
    setState((s) => ({ ...s, [slug]: { ...s[slug], [field]: value } }));

  const appendGallery = (slug, url) =>
    setState((s) => {
      const cur = (s[slug] && s[slug].galleryText) || "";
      const next = cur ? cur + "\n" + url : url;
      return { ...s, [slug]: { ...s[slug], galleryText: next } };
    });

  const save = async (slug) => {
    setSavingSlug(slug);
    setSavedSlug("");
    try {
      const entry = state[slug] || { hero: "", galleryText: "" };
      await adminUpdateJourney(slug, {
        hero: entry.hero,
        gallery: entry.galleryText.split("\n").map((g) => g.trim()).filter(Boolean),
      });
      setSavedSlug(slug);
      setTimeout(() => setSavedSlug(""), 2500);
    } finally {
      setSavingSlug("");
    }
  };

  return (
    <div data-testid="admin-journeys">
      <h1 className="font-serif text-4xl text-ink mb-4">Journey Imagery</h1>
      <p className="text-ink/60 font-light mb-10 max-w-2xl">
        Drag &amp; drop an image (or click to choose a file) to set the hero and gallery photos for
        each journey — no URLs needed. You can also paste an image URL. Leave blank to keep the
        default.
      </p>

      <div className="space-y-px">
        {JOURNEY_THEMES.map((t) => {
          const entry = state[t.slug] || { hero: "", galleryText: "" };
          const galleryUrls = entry.galleryText.split("\n").map((g) => g.trim()).filter(Boolean);
          return (
            <div
              key={t.slug}
              data-testid={`journey-admin-${t.slug}`}
              className="border-t border-stone py-10 grid grid-cols-1 lg:grid-cols-12 gap-8"
            >
              <div className="lg:col-span-3">
                <h3 className="font-serif text-2xl text-ink">{t.name}</h3>
                <div className="mt-4 h-28 w-full bg-stone-soft/60 overflow-hidden">
                  {(entry.hero || t.image) && (
                    <img
                      src={entry.hero || t.image}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => (e.currentTarget.style.opacity = 0.2)}
                    />
                  )}
                </div>
              </div>
              <div className="lg:col-span-9 space-y-6">
                <div>
                  <label className="eyebrow text-ink/50 block mb-2">Hero image</label>
                  <UploadZone
                    testid={`journey-hero-upload-${t.slug}`}
                    label="Drag & drop or click to upload a hero image"
                    onUploaded={(url) => update(t.slug, "hero", url)}
                  />
                  <input
                    data-testid={`journey-hero-${t.slug}`}
                    value={entry.hero}
                    onChange={(e) => update(t.slug, "hero", e.target.value)}
                    placeholder="…or paste an image URL"
                    className="mt-2 w-full bg-transparent border-0 border-b border-stone py-2 text-ink/70 text-sm font-light focus:outline-none focus:border-ink"
                  />
                </div>
                <div>
                  <label className="eyebrow text-ink/50 block mb-2">Gallery images (up to 3 shown)</label>
                  <UploadZone
                    testid={`journey-gallery-upload-${t.slug}`}
                    label="Drag & drop or click to add a gallery image"
                    onUploaded={(url) => appendGallery(t.slug, url)}
                  />
                  <textarea
                    data-testid={`journey-gallery-${t.slug}`}
                    value={entry.galleryText}
                    onChange={(e) => update(t.slug, "galleryText", e.target.value)}
                    rows={3}
                    placeholder="…or paste image URLs, one per line"
                    className="mt-2 w-full bg-stone-soft/40 border border-stone p-3 text-sm text-ink/70 font-light focus:outline-none focus:border-ink resize-none"
                  />
                  {galleryUrls.length > 0 && (
                    <div className="mt-3 flex gap-2">
                      {galleryUrls.slice(0, 3).map((u, i) => (
                        <div key={i} className="h-16 w-24 bg-stone-soft/60 overflow-hidden">
                          <img
                            src={u}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => (e.currentTarget.style.opacity = 0.2)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-4">
                  <button
                    data-testid={`journey-save-${t.slug}`}
                    onClick={() => save(t.slug)}
                    disabled={savingSlug === t.slug}
                    className="bg-ink text-ivory px-8 py-3 text-[0.72rem] tracking-[0.16em] uppercase font-semibold hover:bg-[#16294c] transition-colors disabled:opacity-50"
                  >
                    {savingSlug === t.slug ? "Saving…" : "Save"}
                  </button>
                  {savedSlug === t.slug && (
                    <span className="text-brass inline-flex items-center gap-1.5 text-sm">
                      <Check size={16} /> Saved
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
